import { ReactNode, useRef, useState } from 'react'

import {
	Box,
	Button,
	ButtonGroup,
	ButtonGroupProps,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
} from '@mui/material'
import { ArrowDropDownIcon } from '@mui/x-date-pickers'

type DropdownOption = {
	label: string
	onClick: () => void
	disabled?: boolean
}

type Props = {
	options?: DropdownOption[]
	primaryButton: ReactNode
	menuList?: ReactNode
} & ButtonGroupProps

/**
 * A button with a dropdown menu of additional options.
 * The primary button triggers the main action, while the dropdown
 * button reveals a list of secondary actions.
 *
 * @param options - Array of dropdown options with label and onClick handler.
 * @param primaryButton - The main button element.
 * @param menuList - Optional custom menu list element.
 * @param props - Additional ButtonGroupProps for styling and behavior.
 */
export default function DropdownButton({
	options = [],
	primaryButton,
	menuList = null,
	...props
}: Props) {
	const [open, setOpen] = useState(false)
	const anchorRef = useRef<HTMLDivElement>(null)

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement>, index: number) => {
		options[index].onClick()
		setOpen(false)
	}

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen)
	}

	const handleClose = (event: MouseEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return
		}

		setOpen(false)
	}

	return (
		<>
			<ButtonGroup
				{...props}
				ref={anchorRef}
				sx={{
					'& .MuiButton-root': {
						fontSize: (theme) => theme.typography.button.fontSize, // match normal button
					},
				}}
			>
				{primaryButton}
				<Button
					sx={{ px: 0 }}
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-haspopup="menu"
					onClick={handleToggle}
					disabled={!options.length && !menuList}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				sx={{ zIndex: 100 }}
				open={open}
				anchorEl={anchorRef.current}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<Box>
									{menuList ?? (
										<MenuList id="split-button-menu" autoFocusItem>
											{options.map((option, index) => (
												<MenuItem
													disabled={option.disabled}
													key={option.label}
													onClick={(event) => handleMenuItemClick(event, index)}
												>
													{option.label}
												</MenuItem>
											))}
										</MenuList>
									)}
								</Box>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	)
}
